import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/lib/ThemeContext";
import { useAuth } from "@/lib/AuthContext";
import {
  UserCircle, User, CreditCard, Bell, Shield, Sliders,
  HelpCircle, LogOut, ChevronRight,
} from "lucide-react";

const MENU_ITEMS = [
  { id: "profile", label: "Profile", icon: User, path: "/settings/profile" },
  { id: "billing", label: "Account & Billing", icon: CreditCard, path: "/settings/billing" },
  { id: "notifications", label: "Notifications", icon: Bell, path: "/settings/notifications" },
  { id: "security", label: "Security", icon: Shield, path: "/settings/security" },
  { id: "preferences", label: "Preferences", icon: Sliders, path: "/settings/preferences" },
];

export default function UserDropdown() {
  const { tokens: t } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  const initial = (user?.name?.[0] || user?.email?.[0] || "U").toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-all hover:scale-110"
        style={{ background: `${t.accent}20`, color: t.accent }}
        data-testid="user-dropdown-trigger" aria-haspopup="true" aria-expanded={open}>
        {initial}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 rounded-xl overflow-hidden z-[100]"
          style={{ background: t.bgSecondary, border: `1px solid ${t.borderColor}`, boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}
          role="menu" data-testid="user-dropdown-menu">

          {/* User info header */}
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${t.borderColor}` }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold"
                style={{ background: `${t.accent}15`, color: t.accent }}>
                {initial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: t.textPrimary }}>{user?.name || "User"}</div>
                <div className="text-[10px] truncate" style={{ color: t.textMuted }}>{user?.email}</div>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1">
            {MENU_ITEMS.map((item) => (
              <button key={item.id} role="menuitem"
                className="w-full flex items-center gap-2.5 px-4 py-2 text-left transition-all"
                style={{ color: t.textPrimary }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${t.textPrimary}06`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                onClick={() => { navigate(item.path); setOpen(false); }}
                data-testid={`user-menu-${item.id}`}>
                <item.icon className="h-3.5 w-3.5 shrink-0" style={{ color: t.textMuted }} />
                <span className="text-xs font-medium flex-1">{item.label}</span>
                <ChevronRight className="h-3 w-3 shrink-0" style={{ color: `${t.textMuted}50` }} />
              </button>
            ))}
          </div>

          {/* Sign out */}
          <div className="py-1" style={{ borderTop: `1px solid ${t.borderColor}` }}>
            <button role="menuitem"
              className="w-full flex items-center gap-2.5 px-4 py-2 text-left transition-all"
              style={{ color: t.danger }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${t.danger}08`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              onClick={() => { logout(); setOpen(false); }}
              data-testid="user-menu-signout">
              <LogOut className="h-3.5 w-3.5 shrink-0" />
              <span className="text-xs font-medium">Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
